// HOTSLIP install / standalone helpers.
// Captures the beforeinstallprompt event and exposes a method to trigger install,
// plus detects iOS where install must go through the Share menu.
import { useEffect, useState, useCallback } from "react";

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        listeners.forEach((l) => {
            try { l(true); } catch (e) { /* noop */ }
        });
    });
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        listeners.forEach((l) => {
            try { l(false); } catch (e) { /* noop */ }
        });
    });
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true
    );
}

export function isIOS() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function useInstallPrompt() {
    const [canInstall, setCanInstall] = useState(!!deferredPrompt);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        const cb = (avail) => setCanInstall(avail);
        listeners.add(cb);
        return () => { listeners.delete(cb); };
    }, []);

    useEffect(() => {
        const onChange = () => setInstalled(isStandalone());
        if (window.matchMedia) {
            const mq = window.matchMedia("(display-mode: standalone)");
            try { mq.addEventListener("change", onChange); } catch { mq.addListener(onChange); }
            return () => {
                try { mq.removeEventListener("change", onChange); } catch { mq.removeListener(onChange); }
            };
        }
    }, []);

    const triggerInstall = useCallback(async () => {
        if (!deferredPrompt) return { outcome: "unavailable" };
        try {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            deferredPrompt = null;
            setCanInstall(false);
            return choice;
        } catch (e) {
            return { outcome: "error" };
        }
    }, []);

    return { canInstall, installed, isIOS: isIOS(), triggerInstall };
}
